import {
  type JSX,
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';

import { Analytics, getAnalytics, isSupported } from 'firebase/analytics';
import {
  FirebaseApp,
  FirebaseOptions,
  getApps,
  initializeApp,
} from 'firebase/app';
import {
  AppCheck,
  ReCaptchaEnterpriseProvider,
  initializeAppCheck,
} from 'firebase/app-check';

import { BootStatus } from '../components/core/BootStatus';
import { useConfiguration } from './ConfigurationContext';

export type FirebaseProps = {
  firebaseApp: FirebaseApp;
  appCheck: AppCheck;
  /** Absent where the browser blocks the storage Analytics needs. */
  analytics?: Analytics;
};

export const FirebaseContext = createContext<FirebaseProps | null>(null);

export const FirebaseContextProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [firebaseApp, setFirebaseApp] = useState<FirebaseApp>();
  const [appCheck, setAppCheck] = useState<AppCheck>();
  const [analytics, setAnalytics] = useState<Analytics>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  const { configuration } = useConfiguration();

  useEffect(() => {
    let cancelled = false;

    async function initialise() {
      setFailed(false);

      const app =
        getApps().length === 0
          ? initializeApp(configuration.firebaseConfig as FirebaseOptions)
          : getApps()[0] || undefined;

      if (app === undefined) {
        throw new Error('Firebase could not be initialised.');
      }

      if (configuration.environment === 'local') {
        const token = configuration.appCheck?.debugToken;

        Object.assign(window, {
          FIREBASE_APPCHECK_DEBUG_TOKEN: token,
        });
      }

      // Re-initialising App Check on an app that already has it throws, and the
      // app object survives a remount because it is held by the Firebase SDK
      // rather than by this component.
      const appCheck = initializeAppCheck(app, {
        provider: new ReCaptchaEnterpriseProvider(
          configuration.appCheck?.siteKey as string,
        ),

        isTokenAutoRefreshEnabled: true,
      });

      if (cancelled) {
        return;
      }

      setFirebaseApp(app);
      setAppCheck(appCheck);

      // Analytics is the one piece here the app can do without, and the one most
      // likely to be missing: isSupported opens an IndexedDB handle to decide, and
      // getAnalytics throws outright where cookies or storage are blocked — private
      // windows, in-app browsers, locked-down devices. So it is neither awaited
      // before the app is released nor allowed to fail the boot: holding the splash
      // for a probe, or dropping the boot on its result, would deny those users the
      // app to spare them the telemetry. Both consumers treat it as optional.
      isSupported()
        .then((supported) => {
          if (!cancelled && supported) {
            setAnalytics(getAnalytics(app));
          }
        })
        .catch((error) => {
          console.warn('Analytics is unavailable in this browser.', error);
        });
    }

    initialise().catch((error) => {
      console.error('Failed to initialise Firebase.', error);

      if (!cancelled) {
        setFailed(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [configuration, attempt]);

  if (firebaseApp === undefined || appCheck === undefined) {
    return (
      <BootStatus
        failed={failed}
        message="No hemos podido iniciar la sesión con el servidor. Comprueba tu conexión e inténtalo de nuevo."
        onRetry={() => setAttempt((value) => value + 1)}
      />
    );
  }

  return (
    <FirebaseContext.Provider value={{ firebaseApp, appCheck, analytics }}>
      {children}
    </FirebaseContext.Provider>
  );
};

export const useFirebase = () => {
  const context = useContext(FirebaseContext);

  if (!context) {
    throw new Error(
      'useFirebase must be used within a FirebaseContextProvider',
    );
  }

  return context;
};
